/**
 * app.js
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

// Needed for redux-saga es6 generator support
import '@babel/polyfill';

// Import all the third party stuff
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { ConnectedRouter } from 'connected-react-router';
import { Integrations as TracingIntegrations } from '@sentry/tracing';
import FontFaceObserver from 'fontfaceobserver';
import * as Sentry from '@sentry/browser';
import history from 'utils/history';
import 'sanitize.css/sanitize.css';

// Import root app
import App from 'containers/App';

// Import Language Provider
import LanguageProvider from 'containers/LanguageProvider';
import { ThemeProvider } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import defaultTheme from 'utils/customTheme';

// Load the favicon and the .htaccess file
import '!file-loader?name=[name].[ext]!./images/favicon.ico';
import 'file-loader?name=.htaccess!./.htaccess'; // eslint-disable-line import/extensions

import { SnackbarProvider } from 'notistack';
import config, { isDev, isProd } from 'config';
import configureStore from './configureStore';

// Import i18n messages
import { translationMessages } from './i18n';

if (isProd) {
  Sentry.init({
    dsn: 'https://567bed0b8d8a4f4fa539f0a38b31b24b@sentry.io/1847802',
    environment: process.env.SERVER_ENV,
    tracesSampleRate: 0.25,
    integrations: [
      new TracingIntegrations.BrowserTracing({
        tracingOrigins: [config.endpointHost],
      }),
    ],
  });
}

if (!isDev && !isProd) {
  Sentry.init({
    dsn: 'https://567bed0b8d8a4f4fa539f0a38b31b24b@sentry.io/1847802',
    environment: process.env.SERVER_ENV,
  });
}

// Observe loading of Open Sans (to remove open sans, remove the <link> tag in
// the index.html file and this observer)
const openSansObserver = new FontFaceObserver('Open Sans', {});

// When Open Sans is loaded, add a font-family using Open Sans to the body
openSansObserver.load().then(() => {
  document.body.classList.add('fontLoaded');
});

// Create redux store with history
const initialState = {};
const store = configureStore(initialState, history);
const MOUNT_NODE = document.getElementById('app');

const notistackRef = React.createRef();

const notistackAnchor = {
  horizontal: 'right',
  vertical: 'bottom',
};

function renderNotistackAction(key) {
  return (
    <IconButton
      color="inherit"
      size="small"
      onClick={() => {
        notistackRef.current.closeSnackbar(key);
      }}
    >
      <CloseIcon />
    </IconButton>
  );
}

const render = messages => {
  ReactDOM.render(
    <Provider store={store}>
      <LanguageProvider messages={messages}>
        <ConnectedRouter history={history}>
          <ThemeProvider theme={defaultTheme}>
            <SnackbarProvider
              ref={notistackRef}
              action={renderNotistackAction}
              anchorOrigin={notistackAnchor}
              maxSnack={10}
            >
              <App />
            </SnackbarProvider>
          </ThemeProvider>
        </ConnectedRouter>
      </LanguageProvider>
    </Provider>,
    MOUNT_NODE,
  );
};

if (module.hot) {
  // Hot reloadable React components and translation json files
  // modules.hot.accept does not accept dynamic dependencies,
  // have to be constants at compile-time
  module.hot.accept(['./i18n', 'containers/App'], () => {
    ReactDOM.unmountComponentAtNode(MOUNT_NODE);
    render(translationMessages);
  });
}

// Chunked polyfill for browsers without Intl support
if (!window.Intl) {
  new Promise(resolve => {
    resolve(import('intl'));
  })
    .then(() =>
      Promise.all([
        import('intl/locale-data/jsonp/en.js'),
        import('intl/locale-data/jsonp/de.js'),
      ]),
    ) // eslint-disable-line prettier/prettier
    .then(() => render(translationMessages))
    .catch(err => {
      throw err;
    });
} else {
  render(translationMessages);
}

const offline = false;

// Install ServiceWorker and AppCache in the end since
// it's not most important operation and if main code fails,
// we do not want it installed
if (offline && process.env.NODE_ENV === 'production') {
  require('offline-plugin/runtime').install(); // eslint-disable-line global-require
}
